import { createStyles, rem, Text } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MaskedInput from "react-text-mask";
import createNumberMask from "text-mask-addons/dist/createNumberMask";

const useStylesMask = createStyles((theme) => ({
  wrapper: {
    position: "relative",
  },

  wrapperError: {
    marginBottom: rem(40),
  },

  masked: {
    width: "100%",
    minHeight: rem(35),
    paddingLeft: rem(12),
    paddingRight: rem(40),
    border: `${rem(1)} solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.sm,
    fontFamily: "Manrope",
    transition: "border-color 0.2s ease-in-out",

    "&[type='text']": {
      fontSize: theme.fontSizes.sm,
      color: theme.colors.dark[9],
    },

    "&::placeholder": {
      color: theme.colors.gray[5],
      fontSize: theme.fontSizes.sm,
    },

    "&:focus": {
      border: `${rem(1)} solid ${theme.colors.primary[7]}`,
      outline: "none",
    },

    "&:disabled": {
      backgroundColor: theme.colors.gray[1],
      cursor: "not-allowed",
    },
  },

  maskedError: {
    border: `${rem(1)} solid ${theme.colors.red[6]} !important`,
  },

  suffix: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    transition: "opacity 0.2s ease-in-out",
    color: theme.colors.gray[5],
    pointerEvents: "none", // Đảm bảo không bị ảnh hưởng khi click
  },

  errorWrapper: {
    position: "absolute",
    bottom: rem(-22), // Đưa error xuống dưới input
    left: 0,
    width: "100%",
  },

  errorText: {
    color: theme.colors.red[6],
    fontSize: rem(12),
    marginTop: rem(4),
  },
}));

const defaultMaskOptions = {
  prefix: "",
  suffix: "",
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ".",
  allowDecimal: true,
  decimalSymbol: ",",
  decimalLimit: 2,
  integerLimit: 12,
  allowNegative: false,
  allowLeadingZeroes: false,
};

interface CurrencyInputProps {
  value?: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  type?: string;
  error?: string; // Thêm error vào props
  rightSection?: React.ReactNode;
  thousandSeparator?: "." | ",";
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  type = "text",
  error,
  rightSection,
  thousandSeparator = ".",
}) => {
  const { t } = useTranslation();
  const { classes: classesMask } = useStylesMask();

  const decimalSymbol = thousandSeparator === "," ? "." : ",";
  const locale = thousandSeparator === "," ? "en-US" : "de-DE";
  const currencyMask = createNumberMask({
    ...defaultMaskOptions,
    thousandsSeparatorSymbol: thousandSeparator,
    decimalSymbol,
  });

  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (value !== undefined && value !== null && value !== "") {
      const formatted = new Intl.NumberFormat(locale).format(Number(value));
      setInputValue(formatted);
    } else {
      setInputValue("");
    }
  }, [value, locale]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const stripped = e.target.value.split(thousandSeparator).join("");
    const rawValue =
      decimalSymbol === "."
        ? stripped
        : stripped.split(decimalSymbol).join(".");
    if (rawValue === "") {
      setInputValue("");
      onChange("");
      return;
    }
    const numericValue = Number(rawValue);
    if (Number.isNaN(numericValue)) {
      return;
    }
    setInputValue(new Intl.NumberFormat(locale).format(numericValue));
    onChange(rawValue);
  };

  return (
    <div
      className={`${classesMask.wrapper} ${
        error ? classesMask.wrapperError : ""
      }`}
    >
      <MaskedInput
        className={`${classesMask.masked} ${
          error ? classesMask.maskedError : ""
        }`}
        mask={currencyMask}
        type={type}
        placeholder={placeholder}
        value={inputValue}
        disabled={disabled}
        onChange={handleChange}
      />
      {rightSection ? (
        <div className={classesMask.suffix}>{rightSection}</div>
      ) : (
        <Text
          size="sm"
          fw={500}
          color="gray"
          className={classesMask.suffix}
          style={{ opacity: error ? 0.5 : 1 }}
        >
          {t("VND_CURRENCY")}
        </Text>
      )}

      {error && (
        <div className={classesMask.errorWrapper}>
          <Text size="sm" className={classesMask.errorText}>
            {error}
          </Text>
        </div>
      )}
    </div>
  );
};

export default CurrencyInput;
